import React, { useEffect, useMemo, useRef, useState } from "react";
import { useRegTheme } from "../theme";
import { runTerminalPayment, CARD_TYPE_META } from "../data/terminalPayment";
import { auth } from "../data/api";
import { fmt } from "../uiUtils";

const centsToDollars = (c) => (c == null || c === "" ? 0 : Number(c) / 100);

// Card-present tender via the Payroc PIN pad. Charges `amount` on the terminal and returns the
// result via onApproved — it does NOT save the order (the payment screen records the tender and
// finalizes when the balance is cleared). Rendered as a progress dialog (mirrors Android's
// dialogProgress over AmountToPayFragment). onApproved receives:
//   { paymentId, cardBrand, cvvResult, tipAmount(dollars), authorizedAmount(dollars), raw }
export default function TerminalPaymentModal({ cardType, amount, orderId, idempotencyKey, onApproved, onBack }) {
  const t = useRegTheme();
  const meta = CARD_TYPE_META[cardType] || { label: cardType };

  const canceledRef = useRef(false);
  const [phase, setPhase] = useState("running"); // running | declined | failed
  const [statusText, setStatusText] = useState("Starting…");
  const [message, setMessage] = useState("");
  const [attempt, setAttempt] = useState(0);

  const stageText = useMemo(
    () => ({ creating: "Sending to PIN pad…", waiting: "Present card on the PIN pad", processing: "Processing…" }),
    []
  );

  useEffect(() => {
    let alive = true;
    canceledRef.current = false;
    setPhase("running");
    setMessage("");
    setStatusText("Starting…");

    (async () => {
      const res = await runTerminalPayment({
        orderId,
        amount,
        cardType,
        idempotencyKey,
        operator: auth().employeeName || auth().employeeId || "",
        onStage: (s) => alive && setStatusText(stageText[s] || "Processing…"),
        shouldCancel: () => canceledRef.current,
      });
      if (!alive) return;

      if (res.canceled) {
        onBack && onBack();
        return;
      }
      if (!res.approved) {
        setPhase(res.declined ? "declined" : "failed");
        setMessage(res.reason || "Transaction failed");
        return;
      }
      onApproved &&
        onApproved({
          paymentId: res.paymentId,
          cardBrand: res.cardBrand || "",
          cardLast4: res.cardLast4 || "",
          cvvResult: res.cvvResult || "",
          approvalCode: res.approvalCode || "",
          tipAmount: centsToDollars(res.tipAmount),
          authorizedAmount: centsToDollars(res.authorizedAmount),
          responseMessage: res.responseMessage || "Approved",
          raw: res.raw,
        });
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const s = styles(t);
  const busy = phase === "running";

  return (
    <div className="qv-dlg-overlay" style={s.overlay}>
      <div className="qv-dlg-card" style={s.card} onClick={(e) => e.stopPropagation()}>
        <div style={s.head}>{meta.label}</div>
        <div style={s.body}>
          <div style={s.amount}>{fmt(amount)}</div>
          {busy && (
            <>
              <div style={s.spinner} />
              <div style={s.status}>{statusText}</div>
              <div style={s.hint}>Follow the prompts on the card reader</div>
            </>
          )}
          {phase === "declined" && (
            <>
              <div style={s.iconBad}>✕</div>
              <div style={s.statusBad}>{message || "Transaction Declined!"}</div>
            </>
          )}
          {phase === "failed" && (
            <>
              <div style={s.iconBad}>!</div>
              <div style={s.statusBad}>{message || "Transaction failed"}</div>
            </>
          )}
        </div>
        <div style={s.foot}>
          {busy && (
            <button style={s.cancel} onClick={() => (canceledRef.current = true)}>
              Cancel
            </button>
          )}
          {(phase === "declined" || phase === "failed") && (
            <>
              <button style={s.ghost} onClick={onBack}>
                Back
              </button>
              <button style={s.primary} onClick={() => setAttempt((n) => n + 1)}>
                Retry
              </button>
            </>
          )}
        </div>
      </div>
      <style>{"@keyframes qvspin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}

const styles = (t) => ({
  overlay: { position: "fixed", inset: 0, background: "rgba(2,6,23,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2100 },
  card: { width: "100%", maxWidth: 380, background: t.card || "#111827", color: t.text || "var(--panel-2)", borderRadius: 16, border: `1px solid ${t.cardBorder || "var(--ink)"}`, overflow: "hidden", display: "flex", flexDirection: "column" },
  head: { padding: "14px 18px", fontWeight: 800, fontSize: 16, borderBottom: `1px solid ${t.divider || "var(--ink)"}`, background: t.surfaceAlt || t.headerBg || "#0B1220" },
  body: { padding: 24, display: "flex", flexDirection: "column", alignItems: "center", gap: 14, minHeight: 170, justifyContent: "center" },
  amount: { fontSize: 34, fontWeight: 800, color: t.price || t.text || "var(--panel-2)" },
  spinner: { width: 44, height: 44, borderRadius: "50%", border: `4px solid ${t.divider || "var(--ink)"}`, borderTopColor: t.accent || "#3B82F6", animation: "qvspin 0.8s linear infinite" },
  status: { fontSize: 16, fontWeight: 500, textAlign: "center" },
  statusBad: { fontSize: 16, fontWeight: 500, textAlign: "center", color: "#EF4444" },
  hint: { fontSize: 13, color: t.textMuted || "var(--ink-3)", textAlign: "center" },
  iconBad: { width: 48, height: 48, borderRadius: "50%", background: "#EF4444", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, fontWeight: 800 },
  foot: { padding: 14, display: "flex", gap: 10, borderTop: `1px solid ${t.divider || "var(--ink)"}`, justifyContent: "flex-end" },
  cancel: { flex: 1, height: 46, borderRadius: 10, border: `1px solid ${t.cardBorder || "var(--ink)"}`, background: "transparent", color: t.text || "var(--panel-2)", fontWeight: 500, cursor: "pointer" },
  ghost: { minWidth: 100, height: 46, borderRadius: 10, border: `1px solid ${t.cardBorder || "var(--ink)"}`, background: "transparent", color: t.text || "var(--panel-2)", fontWeight: 500, cursor: "pointer" },
  primary: { minWidth: 120, height: 46, borderRadius: 10, border: "none", background: t.accent || "#3B82F6", color: "#fff", fontWeight: 800, cursor: "pointer" },
});
